import React, { useState, useRef, useEffect } from "react";
import { Bot, X, Send, Loader2, Mic, MicOff } from "lucide-react";
import axios from "axios";
import authService from "../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoboFlutuante = ({ currentSection, setCurrentSection }) => {
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [temAlerta, setTemAlerta] = useState(false);
  const [ouvindo, setOuvindo] = useState(false);
  const fimRef = useRef(null);

  useEffect(() => {
    if (fimRef.current) fimRef.current.scrollIntoView({ behavior: "smooth" });
  }, [mensagens]);

  useEffect(() => {
    const verificar = async () => {
      try {
        const token = authService.getToken();
        if (!token) return;
        const resp = await axios.get(`${API}/chat/alertas`, { headers: { Authorization: `Bearer ${token}` } });
        setTemAlerta((resp.data.alertas || []).length > 0);
      } catch (error) {}
    };
    verificar();
  }, []);

  function iniciarEscuta() {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      alert("Seu navegador nao suporta reconhecimento de voz. Tente usar o Chrome.");
      return;
    }
    const reconhecimento = new SpeechRecognitionAPI();
    reconhecimento.lang = "pt-BR";
    reconhecimento.continuous = false;
    reconhecimento.interimResults = false;
    reconhecimento.onstart = () => setOuvindo(true);
    reconhecimento.onend = () => setOuvindo(false);
    reconhecimento.onerror = () => setOuvindo(false);
    reconhecimento.onresult = (event) => {
      const textoFalado = event.results[0][0].transcript;
      setTexto(textoFalado);
    };
    reconhecimento.start();
  }

  function falarResposta(texto) {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const fala = new SpeechSynthesisUtterance(texto);
    fala.lang = "pt-BR";
    fala.rate = 1.05;
    window.speechSynthesis.speak(fala);
  }

  const enviar = async (e) => {
    e.preventDefault();
    const msg = texto.trim();
    if (!msg || carregando) return;
    setTexto("");
    setMensagens((prev) => [...prev, { role: "user", content: msg }]);
    setCarregando(true);
    try {
      const token = authService.getToken();
      const resp = await axios.post(`${API}/chat`, {
        message: msg,
        conversation_id: conversationId,
        contexto_painel: currentSection
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversationId(resp.data.conversation_id);
      setMensagens((prev) => [...prev, { role: "assistant", content: resp.data.assistant_message.content, acaoSugerida: resp.data.acao_sugerida }]);
      falarResposta(resp.data.assistant_message.content);
    } catch (error) {
      setMensagens((prev) => [...prev, { role: "assistant", content: "Nao consegui responder agora. Tente novamente em instantes." }]);
    } finally {
      setCarregando(false);
    }
  };

  if (!aberto) {
    return (
      <button
        onClick={() => setAberto(true)}
        className="fixed bottom-6 right-6 z-[9999] w-20 h-20 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 shadow-2xl flex items-center justify-center hover:scale-105 transition-transform relative"
        title="Falar com o Robo ClickHub"
      >
        <Bot className="w-10 h-10 text-white" />
        {temAlerta && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse" />
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-80 sm:w-96 h-[28rem] bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="flex items-center gap-2">
          <Bot className={`w-5 h-5 text-white ${carregando ? "animate-pulse" : ""}`} />
          <div className="flex flex-col">
            <span className="text-white font-semibold text-sm">Robo ClickHub</span>
            <span className="text-white/70 text-[10px]">
              {carregando ? "pensando..." : ouvindo ? "ouvindo..." : "online"}
            </span>
          </div>
        </div>
        <button onClick={() => setAberto(false)} className="text-white/80 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {mensagens.length === 0 && (
          <p className="text-gray-500 text-xs text-center mt-6">
            Pergunte sobre suas vendas, produtos ou como usar o sistema.
          </p>
        )}
        {mensagens.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === "assistant" ? "bg-gray-800 text-gray-100" : "bg-blue-600 text-white"}`}>
              {m.content}
              {m.acaoSugerida && m.acaoSugerida.secao_destino && (
                <button
                  onClick={() => setCurrentSection && setCurrentSection(m.acaoSugerida.secao_destino)}
                  className="mt-2 block w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg px-3 py-2"
                >
                  Conectar agora
                </button>
              )}
            </div>
          </div>
        ))}
        {carregando && (
          <div className="flex justify-start">
            <div className="bg-gray-800 rounded-xl px-3 py-2">
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            </div>
          </div>
        )}
        <div ref={fimRef} />
      </div>
      <form onSubmit={enviar} className="flex items-center gap-2 p-3 border-t border-gray-800">
        <button
          type="button"
          onClick={iniciarEscuta}
          className={`rounded-lg p-2 ${ouvindo ? "bg-red-600 animate-pulse" : "bg-gray-800 hover:bg-gray-700"}`}
          title="Falar com o robo"
        >
          {ouvindo ? <MicOff className="w-4 h-4 text-white" /> : <Mic className="w-4 h-4 text-white" />}
        </button>
        <input
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Digite sua pergunta..."
          className="flex-1 bg-gray-800 text-white text-sm rounded-lg px-3 py-2 outline-none border border-gray-700 focus:border-blue-500"
          disabled={carregando}
        />
        <button type="submit" disabled={carregando || !texto.trim()} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg p-2">
          <Send className="w-4 h-4 text-white" />
        </button>
      </form>
    </div>
  );
};

export default RoboFlutuante;
